import React from 'react'
import { Accordion, Container, Row, Col } from 'react-bootstrap'
import { modifySettingDetail } from '..'
import { ButtonModals } from '../../common'
import { openModal, closeModal } from '../settingActions'
import {
  createArticleRulesGroup,
  updateArticleRulesGroup,
  deleteArticleRulesGroup,
  updateArticleRulesPageSetting
} from '../settingAPI'
import { UpdateArticleRulesPageSettingForm } from './articleRules/UpdateArticleRulesPageSettingForm'
import { UpsertArticleRulesGroupForm } from './articleRules/UpsertArticleRulesGroupForm'
import { DeleteArticleRulesGroupForm } from './articleRules/DeleteArticleRulesGroupForm'
import { ArticleRulePatternParts } from './articleRules/ArticleRulePatternParts'

type PatternRule = Record<string, unknown> & { priority: string }

type ArticleRulesGroup = {
  id: number
  name: string
  pageNumbers?: number[]
  patterns?: Record<string, PatternRule[]>
}

type Values = {
  articleRulesGroups: ArticleRulesGroup[]
}

type CurrentUser = {
  role: string
}

type SettingProp = {
  id: number
}

type Props = {
  values: Values
  isSettingWritable: boolean
  openedModal: string | null
  currentUser: CurrentUser
  setting: SettingProp
  dispatch: (action: unknown) => void
  enumValues: { priorities: Record<string, string>; [key: string]: Record<string, string> }
}

export const ArticleRuleSettingForm = ({
  values,
  isSettingWritable,
  openedModal,
  currentUser,
  setting,
  dispatch,
  enumValues
}: Props) => {
  const handleSubmit = ({ id, ...rest }: { id: number; [key: string]: unknown }) => {
    const snakeSettingName = 'article_rules'
    const articleRule = { id, ...rest }
    dispatch(modifySettingDetail({ settingId: setting.id, settingName: snakeSettingName, id, articleRule }))
  }
  const handleUpdateArticleRulesPageSettingSubmit = ({
    pageNumbers,
    settingId,
    groupId
  }: {
    pageNumbers: string
    settingId: number
    groupId: number
  }) => {
    const submitParams = { settingId, groupId, pageNumbers: pageNumbers.split(',').map((s) => parseInt(s.trim(), 10)) }
    dispatch(updateArticleRulesPageSetting(submitParams))
  }
  const handleCreateArticleRulesGroupSubmit = ({
    name,
    pageNumbers,
    settingId
  }: {
    name: string
    pageNumbers: string
    settingId: number
  }) => {
    const submitParams = { settingId, name, pageNumbers: pageNumbers.split(',').map((s) => parseInt(s.trim(), 10)) }
    dispatch(createArticleRulesGroup(submitParams))
  }
  const handleUpdateArticleRulesGroupSubmit = ({
    name,
    pageNumbers,
    settingId,
    id
  }: {
    name: string
    pageNumbers: string
    settingId: number
    id: number
  }) => {
    const submitParams = { settingId, id, name, pageNumbers: pageNumbers.split(',').map((s) => parseInt(s.trim(), 10)) }
    dispatch(updateArticleRulesGroup(submitParams))
  }
  const handleDeleteArticleRulesGroupSubmit = ({
    name: _name,
    settingId,
    id
  }: {
    name: string
    settingId: number
    id: number
  }) => {
    dispatch(deleteArticleRulesGroup({ settingId, id }))
  }

  const disabled = currentUser.role !== 'group_administrator' || !isSettingWritable
  const getButtonModalSetting = (grp: ArticleRulesGroup) => {
    if (grp.name === 'sub') {
      return
    }
    if (grp.name === 'top') {
      const modalName = 'editPageNumberFor-top-grp'
      return [
        {
          name: modalName,
          button: {
            handleClick: () => dispatch(openModal(modalName)),
            disabled,
            as: 'a' as React.ElementType,
            icon: 'file-pen',
            text: '対象ページ指定の変更'
          },
          form: {
            initialValues: {
              settingId: setting.id,
              pageNumbers: grp.pageNumbers?.join(', '),
              groupId: grp.id
            },
            handleSubmit: handleUpdateArticleRulesPageSettingSubmit,
            Component: UpdateArticleRulesPageSettingForm,
            validationSchema: UpdateArticleRulesPageSettingForm.schema,
            props: {
              title: '対象ページ指定の変更',
              onClickCancel: () => dispatch(closeModal()),
              submitButtonText: '変更する',
              enumValues,
              isSettingWritable,
              setting
            }
          }
        }
      ]
    }
    const upsertModalName = `upsertArticleRulesGroupModal-grp-${grp.id}`
    const deleteModalName = `deleteArticleRulesGroupModal-grp-${grp.id}`
    return [
      {
        name: upsertModalName,
        button: {
          handleClick: () => dispatch(openModal(upsertModalName)),
          disabled,
          as: 'a' as React.ElementType,
          icon: 'file-pen',
          text: '組版ルールグループの変更'
        },
        form: {
          initialValues: {
            id: grp?.id,
            name: grp?.name ?? '',
            pageNumbers: grp?.pageNumbers?.join(',') ?? '',
            settingId: setting.id
          },
          handleSubmit: handleUpdateArticleRulesGroupSubmit,
          Component: UpsertArticleRulesGroupForm,
          validationSchema: UpsertArticleRulesGroupForm.schema,
          props: {
            title: '組版ルールグループの変更',
            onClickCancel: () => dispatch(closeModal()),
            submitButtonText: '変更する',
            enumValues,
            isSettingWritable,
            setting
          }
        }
      },
      {
        name: deleteModalName,
        button: {
          handleClick: () => dispatch(openModal(deleteModalName)),
          disabled,
          variant: 'outline-danger',
          as: 'a' as React.ElementType,
          icon: 'trash-can',
          text: '組版ルールグループの削除'
        },
        form: {
          initialValues: {
            id: grp?.id,
            name: grp?.name ?? '',
            settingId: setting.id
          },
          handleSubmit: handleDeleteArticleRulesGroupSubmit,
          Component: DeleteArticleRulesGroupForm,
          props: {
            title: '組版ルールグループの削除',
            onClickCancel: () => dispatch(closeModal()),
            submitButtonText: '削除する',
            enumValues,
            isSettingWritable,
            setting
          }
        }
      }
    ]
  }
  const createNewArticleRuleGroupButtonModalSettings = [
    {
      name: 'createNewArticleRuleGroupButtonModal',
      button: {
        icon: 'file-circle-plus',
        handleClick: () => dispatch(openModal('createNewArticleRuleGroupButtonModal')),
        disabled,
        text: '組版ルールグループの追加'
      },
      form: {
        initialValues: {
          name: '',
          pageNumbers: '',
          settingId: setting.id
        },
        handleSubmit: handleCreateArticleRulesGroupSubmit,
        Component: UpsertArticleRulesGroupForm,
        validationSchema: UpsertArticleRulesGroupForm.schema,
        props: {
          title: '組版ルールグループの追加',
          onClickCancel: () => dispatch(closeModal()),
          submitButtonText: '追加する',
          enumValues,
          isSettingWritable,
          setting
        }
      }
    }
  ]
  const patternDescriptionStrings = [
    'パターン1（カット見出し横）',
    'パターン2（カット見出し縦）',
    'パターン3（カット見出しなし）'
  ]
  const groupNames: Record<string, string> = {
    top: 'トップ',
    sub: 'それ以外の面'
  }
  const articleRulesGroups = [...values.articleRulesGroups]
  return (
    <>
      <ButtonModals
        key='create-new-group-modal'
        settings={createNewArticleRuleGroupButtonModalSettings}
        openedModal={openedModal}
        className='mb-3'
      />

      <Accordion defaultActiveKey='top' className='mb-3'>
        {articleRulesGroups.map((grp) => {
          const pageNumbersStr = grp?.pageNumbers?.map((n) => `${n}面`)?.join('、')
          const accordionTitle = `${groupNames[grp.name] ?? grp.name}${pageNumbersStr ? `（対応ページ：${pageNumbersStr}）` : ''}`
          const buttonModalSettings = getButtonModalSetting(grp)
          return (
            <React.Fragment key={`${grp.name}-accordion`}>
              <Accordion.Item eventKey={grp.name} key={`${grp.name}-item`}>
                <Accordion.Header key={`${grp.name}-header`}>
                  <Container>
                    <Row className='align-items-center'>
                      <Col sm={8}> {accordionTitle} </Col>
                      <Col className='ms-auto'>
                        {buttonModalSettings && (
                          <ButtonModals
                            key={`${grp.name}-modal}`}
                            settings={buttonModalSettings}
                            openedModal={openedModal}
                          />
                        )}
                      </Col>
                    </Row>
                  </Container>
                </Accordion.Header>
                <Accordion.Body key={`${grp.name}-body`}>
                  {Object.keys(grp?.patterns ?? {}).map((pattern, patternIdx) => (
                    <Accordion key={`${grp.name}-${pattern}`}>
                      <Accordion.Item eventKey={`${grp.name}-${pattern}`} key={`${grp.name}-${pattern}-item`}>
                        <Accordion.Header key={`${grp.name}-${pattern}-header`}>
                          {patternDescriptionStrings[patternIdx]}
                        </Accordion.Header>
                        <Accordion.Body key={`${grp.name}-${pattern}-body`}>
                          <ArticleRulePatternParts
                            key={`${grp.name}-${patternIdx}`}
                            patternType={grp.name}
                            patternIdx={patternIdx}
                            patternRules={grp?.patterns?.[pattern] ?? []}
                            enumValues={enumValues}
                            handleSubmit={handleSubmit}
                            isSettingWritable={isSettingWritable}
                          />
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            </React.Fragment>
          )
        })}
      </Accordion>
    </>
  )
}
